import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreateTaxAdjustment,
  TaxAdjCategory,
  TaxAdjStatus,
  TaxAdjSummary,
  TaxAdjustment,
  UpdateTaxAdjustment,
} from '@dcc-erp/shared';

import { api } from '../api';

// Cache-key scopes — keyed by companyId so DCC/TTOP/RCI switches give
// independent caches (matches prepaid.ts / ar.ts).
const listKey = (companyId: string | undefined) => ['tax-adjustments', companyId] as const;
const summaryKey = (companyId: string | undefined, taxYear: number | undefined) =>
  ['tax-adjustments', companyId, 'summary', taxYear] as const;

export interface TaxAdjFilters {
  taxYear?: number;
  category?: TaxAdjCategory;
  status?: TaxAdjStatus;
}

export function useTaxAdjustments(companyId: string | undefined, filters: TaxAdjFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.taxYear !== undefined) qs.set('taxYear', String(filters.taxYear));
  if (filters.category) qs.set('category', filters.category);
  if (filters.status) qs.set('status', filters.status);
  const search = qs.toString();
  return useQuery({
    queryKey: [...listKey(companyId), filters],
    queryFn: () =>
      api<TaxAdjustment[]>(`/companies/${companyId}/tax-adjustments${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useTaxAdjustment(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['tax-adjustments', companyId, id],
    queryFn: () => api<TaxAdjustment>(`/companies/${companyId}/tax-adjustments/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useTaxAdjSummary(companyId: string | undefined, taxYear: number | undefined) {
  return useQuery({
    queryKey: summaryKey(companyId, taxYear),
    queryFn: () =>
      api<TaxAdjSummary>(`/companies/${companyId}/tax-adjustments/summary?taxYear=${taxYear}`),
    enabled: !!companyId && taxYear !== undefined,
  });
}

export function useCreateTaxAdjustment(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTaxAdjustment) =>
      api<TaxAdjustment>(`/companies/${companyId}/tax-adjustments`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKey(companyId) });
    },
  });
}

export function useUpdateTaxAdjustment(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTaxAdjustment }) =>
      api<TaxAdjustment>(`/companies/${companyId}/tax-adjustments/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKey(companyId) });
    },
  });
}

export function useDeleteTaxAdjustment(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ id: string; deleted: boolean }>(`/companies/${companyId}/tax-adjustments/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKey(companyId) });
    },
  });
}

export function useApproveTaxAdjustment(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<TaxAdjustment>(`/companies/${companyId}/tax-adjustments/${id}/approve`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: listKey(companyId) });
    },
  });
}
